"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Car, Eye, EyeOff, Loader2, Store, ShieldCheck, Phone } from "lucide-react";

const API = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

type LoginMode = "email" | "phone";

export default function LoginPage() {
    const router = useRouter();
    const [loginMode, setLoginMode] = useState<LoginMode>("phone");
    const [loginValue, setLoginValue] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const isPublicCustomerLogin = loginMode === "phone";

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            let res: Response;
            let data: any;

            if (isPublicCustomerLogin) {
                // Public customer: login via phone or email with password
                res = await fetch(`${API}/api/auth/public-login/`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ login: loginValue, password }),
                });
                data = await res.json();
            } else {
                // Vendor/Admin: login via email + password (existing endpoint)
                res = await fetch(`${API}/api/auth/login/`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: loginValue, password }),
                });
                data = await res.json();
            }

            if (!res.ok) {
                const msg =
                    data.non_field_errors?.[0] ||
                    data.detail ||
                    data.login?.[0] ||
                    "بيانات الدخول غير صحيحة. حاول مجدداً.";
                setError(msg);
                return;
            }

            // Store tokens
            localStorage.setItem("s360_access", data.access);
            localStorage.setItem("s360_refresh", data.refresh);
            if (data.user) {
                localStorage.setItem("s360_user", JSON.stringify(data.user));
            }

            const role = data.user?.role;
            if (role === "admin" || role === "superadmin") {
                router.push("/admin");
            } else if (role === "vendor") {
                router.push("/profile");
            } else {
                router.push("/customer-dashboard");
            }
        } catch {
            setError("خطأ في الاتصال بالخادم. تأكد من تشغيل النظام.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center p-4">
            {/* Background decoration */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 -left-24 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 -right-24 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
            </div>

            <div className="relative w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-3xl shadow-2xl shadow-blue-900/50 mb-4">
                        <Car className="h-10 w-10 text-white" />
                    </div>
                    <h1 className="text-3xl font-black text-white">سيارتي 360</h1>
                    <p className="text-blue-300 text-sm mt-1">منصة السيارات المتكاملة</p>
                </div>

                {/* Card */}
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
                    <h2 className="text-xl font-bold text-white text-center mb-2">تسجيل الدخول</h2>

                    {/* Mode Toggle */}
                    <div className="flex rounded-2xl bg-white/10 p-1 mb-6">
                        <button
                            type="button"
                            onClick={() => { setLoginMode("phone"); setLoginValue(""); }}
                            className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-2 text-sm font-bold transition-all ${loginMode === "phone" ? "bg-blue-600 text-white shadow" : "text-white/50 hover:text-white"}`}
                        >
                            <Phone className="h-4 w-4" />
                            <span>مستخدم عام</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => { setLoginMode("email"); setLoginValue(""); }}
                            className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-2 text-sm font-bold transition-all ${loginMode === "email" ? "bg-orange-500 text-white shadow" : "text-white/50 hover:text-white"}`}
                        >
                            <Store className="h-4 w-4" />
                            <span>تاجر / إدارة</span>
                        </button>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-blue-200 mb-1.5">
                                {loginMode === "phone" ? "رقم الجوال أو البريد الإلكتروني" : "البريد الإلكتروني"}
                            </label>
                            <input
                                type={loginMode === "email" ? "email" : "text"}
                                value={loginValue}
                                onChange={(e) => setLoginValue(e.target.value)}
                                required
                                dir="ltr"
                                className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                                placeholder={loginMode === "phone" ? "+966501234567 أو example@email.com" : "vendor@email.com"}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-blue-200 mb-1.5">كلمة المرور</label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    dir="ltr"
                                    className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition pr-12"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition"
                                >
                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                            {loginMode === "phone" && (
                                <div className="text-right mt-1.5">
                                    <Link href="/forgot-password" className="text-blue-400 hover:underline text-xs">
                                        نسيت كلمة المرور؟
                                    </Link>
                                </div>
                            )}
                        </div>

                        {error && (
                            <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-3 text-red-300 text-sm text-center">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white font-bold rounded-2xl py-3.5 text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-blue-900/50"
                        >
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                            {loading ? "جاري الدخول..." : "تسجيل الدخول"}
                        </button>
                    </form>

                    {/* Info section */}
                    <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                        <div className="flex items-center gap-3 text-white/60 text-xs">
                            <Store className="h-4 w-4 text-orange-400 flex-shrink-0" />
                            <span>لوحة تحكم التجار المعتمدين والورش</span>
                        </div>
                        <div className="flex items-center gap-3 text-white/60 text-xs">
                            <ShieldCheck className="h-4 w-4 text-green-400 flex-shrink-0" />
                            <span>إدارة متكاملة للموافقات والمدفوعات</span>
                        </div>
                    </div>

                    <p className="text-center text-white/40 text-xs mt-4">
                        ليس لديك حساب؟{" "}
                        <Link href="/register" className="text-orange-400 hover:underline">
                            سجّل كتاجر/ورشة
                        </Link>
                        {" · "}
                        <Link href="/public-register" className="text-blue-400 hover:underline">
                            إنشاء حساب عميل
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
